/**
 * ProjectionStatusBadge Component
 *
 * Displays the status of a projection based on editing window:
 * - Draft (yellow): Within editing window (Oct 1 - Dec 31, ET) for this year
 * - Final (green with lock): Outside editing window
 */

import React from 'react';
import { LockClosedIcon, PencilSquareIcon, LockOpenIcon } from '@heroicons/react/24/outline';
import { ProjectionStatus, EditingWindowStatus } from '../../types/projections';

interface ProjectionStatusBadgeProps {
  status: ProjectionStatus;
  editingWindowStatus?: EditingWindowStatus;
  lastEditedBy?: string;
  lastEditedAt?: string;
}

export const ProjectionStatusBadge: React.FC<ProjectionStatusBadgeProps> = ({
  status,
  editingWindowStatus,
  lastEditedBy,
  lastEditedAt,
}) => {
  // Final status with executive override
  if (status === 'final' && editingWindowStatus?.hasExecutiveOverride) {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm font-medium">
        <LockOpenIcon className="h-4 w-4" />
        <span>Final (Override Active)</span>
      </div>
    );
  }

  // Final status (outside editing window)
  if (status === 'final') {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-medium">
        <LockClosedIcon className="h-4 w-4" />
        <span>Final</span>
      </div>
    );
  }

  // Draft status (within editing window)
  return (
    <div className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-yellow-100 text-yellow-800 rounded-full text-sm font-medium">
      <PencilSquareIcon className="h-4 w-4" />
      <span>Draft</span>
    </div>
  );
};
